import { useEffect, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { Icon, type IconName } from './Icon'
import './ui.css'

/* ------------------------------- Banner --------------------------------- */

export function Banner({
  title,
  children,
  tone,
  icon = 'info',
  action,
}: {
  title?: string
  children: ReactNode
  tone?: 'accent' | 'danger'
  icon?: IconName
  action?: ReactNode
}) {
  return (
    <div className={clsx('banner', tone && 'banner--' + tone)}>
      <span className="banner__icon">
        <Icon name={icon} size={17} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <strong className="banner__title">{title}</strong>}
        {children}
        {action && <div style={{ marginBlockStart: 'var(--s3)' }}>{action}</div>}
      </div>
    </div>
  )
}

/* ----------------------------- Empty state ------------------------------ */

export function EmptyState({
  title,
  body,
  icon,
  illustration = 'peek',
  action,
}: {
  title: string
  body?: string
  /** An icon in a glass badge, used instead of the illustration when given. */
  icon?: IconName
  /** The jelly cat: peeking in (empty), or happy (something just went well). */
  illustration?: 'peek' | 'happy' | 'none'
  action?: ReactNode
}) {
  return (
    <div className="empty">
      {icon || illustration === 'none' ? (
        <div className="empty__mount">
          <Icon name={icon ?? 'cat'} size={34} />
        </div>
      ) : (
        <img
          className="empty__illo"
          src={illustration === 'happy' ? '/illustrations/cat-happy.jpg' : '/illustrations/cat-peek.jpg'}
          alt=""
          width={132}
          height={132}
          decoding="async"
        />
      )}
      <h3 className="empty__title">{title}</h3>
      {body && <p className="empty__body">{body}</p>}
      {action && <div style={{ marginBlockStart: 'var(--s3)' }}>{action}</div>}
    </div>
  )
}

/* ------------------------------ Skeleton -------------------------------- */

export function Skeleton({
  height = 16,
  width = '100%',
  radius,
  className,
}: {
  height?: number | string
  width?: number | string
  radius?: string
  className?: string
}) {
  return (
    <span
      className={clsx('skeleton', className)}
      style={{ display: 'block', height, width, borderRadius: radius }}
      aria-hidden="true"
    />
  )
}

export function PlateSkeleton({ width = '100%' }: { width?: number | string }) {
  return (
    <div style={{ width, display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
      <Skeleton height={0} width="100%" className="skeleton-plate" />
      <div style={{ aspectRatio: '4 / 5', width: '100%' }} className="skeleton" />
      <Skeleton height={14} width="70%" radius="4px" />
      <Skeleton height={9} width="90%" radius="3px" />
    </div>
  )
}

/* ------------------------------- Spinner -------------------------------- */

export function Lens({ size = 24 }: { size?: number }) {
  return (
    <span className="lens-spin" role="status" aria-label="Cargando">
      <Icon name="lens" size={size} strokeWidth={1.4} />
    </span>
  )
}

/* -------------------------------- Toasts -------------------------------- */

export type ToastTone = 'neutral' | 'success' | 'error' | 'achievement'
export interface ToastItem {
  id: number
  message: string
  tone: ToastTone
}

let counter = 0
const listeners = new Set<(t: ToastItem) => void>()

/** Fire-and-forget notice. Never used for anything the user must act on. */
export const toast = {
  show(message: string, tone: ToastTone = 'neutral') {
    const item: ToastItem = { id: ++counter, message, tone }
    listeners.forEach((l) => l(item))
  },
  success(message: string) {
    toast.show(message, 'success')
  },
  error(message: string) {
    toast.show(message, 'error')
  },
  /** The sticker lands: a small celebration for a freshly earned achievement. */
  achievement(name: string) {
    toast.show('🏆 ¡Logro conseguido! · ' + name, 'achievement')
  },
}

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([])
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const onToast = (t: ToastItem) => {
      setItems((prev) => [...prev.slice(-2), t])
      const timer = setTimeout(() => {
        setItems((prev) => prev.filter((i) => i.id !== t.id))
        timers.current.delete(t.id)
      }, 4200)
      timers.current.set(t.id, timer)
    }
    listeners.add(onToast)
    const map = timers.current
    return () => {
      listeners.delete(onToast)
      map.forEach(clearTimeout)
      map.clear()
    }
  }, [])

  if (items.length === 0) return null
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={clsx('toast', t.tone !== 'neutral' && 'toast--' + t.tone)}>
          {t.tone === 'success' && <Icon name="check" size={15} strokeWidth={2} />}
          {t.tone === 'error' && <Icon name="warning" size={15} strokeWidth={2} />}
          {t.message}
        </div>
      ))}
    </div>
  )
}
